import i18n from '@/js/i18n.js';
import { newTransactionReference } from '@/js/api/payments.js';
import { usePaymentTransactionStore } from '@/js/pinia-stores/paymentTransaction.js';
import { useKeycloakStore } from '@/js/pinia-stores/keycloak.js';

/**
 * Unified payment/payout triggers.
 *
 * One intent-based method per business flow (campaign contribution,
 * ad top-up, listing purchase, cart checkout, wallet top-up, payout).
 * Every method builds a canonical PaymentTransactionRequest draft plus a
 * display context, persists it in the paymentTransaction store, then hands
 * off to the unified /pay/:reference page which owns provider selection
 * (mobile money, card, wallet transfer...) and step-up authentication.
 *
 * Mirrors market-standard checkout patterns (Stripe Checkout / Shopify):
 * collect intent where the user is, pay in one dedicated, resumable screen.
 */
const t = i18n.global.t;

/** Legacy trxType -> EntityType per backend PaymentTransactionRequest docs */
const TRX_TYPE_TO_ENTITY = {
  WALL: 'WALLET_TOPUP',
  TONT: 'CONTRIBUTION',
  ASSO: 'CAMPAIGN',
  SAVB: 'SAVE_TO_BUY',
  GBUY: 'GROUP_BUY',
};

/**
 * Campaign type (see constants/campaign-capabilities.js) -> EntityType.
 * Tontine/savings/subscriptions produce per-cycle contributions;
 * fund collects/donations/etc. fund a campaign cagnotte.
 */
const CAMPAIGN_TYPE_TO_ENTITY = {
  GROUP_BUY: 'GROUP_BUY',
  SAVE_TO_BUY: 'SAVE_TO_BUY',
  COOP_PURCHASE: 'GROUP_BUY',
  PREORDER: 'ORDER',
  TONTINE: 'CONTRIBUTION',
  GROUP_SAVINGS: 'CONTRIBUTION',
  SUBSCRIPTION: 'SUBSCRIPTION',
  MEMBERSHIP: 'CONTRIBUTION',
  TICKETING: 'ORDER',
  EVENT_REGISTRATION: 'ORDER',
  RAFFLE: 'ORDER',
};

export function usePaymentTrigger({ f7router } = {}) {
  const paymentTxStore = usePaymentTransactionStore();
  const keycloakStore = useKeycloakStore();

  // ============================================
  // Internals
  // ============================================
  function _requireAmount(amount) {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      throw new Error(t('common.enterValidAmount'));
    }
    return value;
  }

  function _baseRequest({
    amount,
    currency = 'XOF',
    paymentType = 'PAY_IN',
    entityType,
    entityId,
    description = '',
    referenceNote = '',
    metadata = {},
  }) {
    return {
      transactionReference: newTransactionReference(),
      amount,
      fees: 0,
      currency,
      paymentType,
      // paymentPartner/paymentMethod stay unchosen here — the user picks
      // them on the unified payment page before anything is submitted.
      entityType,
      entityId,
      groupId: metadata.groupId,
      campaignId: metadata.campaignId,
      adCampaignId: metadata.adCampaignId,
      orderId: metadata.orderId,
      listingId: metadata.listingId,
      shopId: metadata.shopId,
      contributionId: metadata.contributionId,
      userId: keycloakStore.subject || undefined,
      beneficiaryWalletId: metadata.beneficiaryWalletId,
      description,
      referenceNote,
      returnUrl: `${window.location.origin}/transaction/sucess/`,
      cancelUrl: `${window.location.origin}/transaction/failure/`,
      metadata: { ...metadata, source: metadata.source || 'payment-trigger' },
    };
  }

  /**
   * Persists the draft and navigates to the unified payment page.
   * Returns the transaction reference so callers can correlate.
   */
  function _launch(request, display = {}) {
    const reference = request.transactionReference;
    paymentTxStore.setDraft({
      reference,
      createdAt: Date.now(),
      request,
      display: {
        title: display.title || '',
        subtitle: display.subtitle || '',
        icon: display.icon || 'creditcard_fill',
        ...display,
      },
    });
    if (f7router?.navigate) {
      f7router.navigate(`/pay/${reference}`);
    } else {
      f7.views?.main?.router?.navigate(`/pay/${reference}`);
    }
    return reference;
  }

  // ============================================
  // Campaigns & groups
  // ============================================

  /**
   * Contribute to a tontine cycle, association pot, savings project,
   * group-buy or generic campaign cagnotte.
   *
   * @param {object} p
   * @param {string} p.campaignId     Target campaign/project id
   * @param {string} [p.groupId]      Owning group id (tontine/asso/groupBuy)
   * @param {number|string} p.amount
   * @param {string} [p.campaignType] Semantic type (TONTINE, GROUP_BUY, FUND_COLLECT...)
   * @param {string} [p.trxType]      Legacy short code (TONT|ASSO|SAVB|GBUY|WALL)
   * @param {string} [p.currency]
   * @param {string} [p.campaignName] Display name of the campaign
   * @param {string} [p.groupName]    Display name of the owning group
   * @param {boolean} [p.anonymous]
   * @param {string} [p.tag]          Payment tag/reference note
   * @param {string} [p.description]
   */
  function triggerCampaignContribution({
    campaignId,
    groupId,
    amount,
    campaignType = '',
    trxType = '',
    currency = 'XOF',
    campaignName = '',
    groupName = '',
    anonymous = false,
    tag = '',
    description = '',
    category = '',
    categoryLabel = '',
  }) {
    if (!campaignId) throw new Error('campaignId is required');
    const value = _requireAmount(amount);
    const normalizedType = String(trxType).toUpperCase();
    const entityType =
      CAMPAIGN_TYPE_TO_ENTITY[campaignType] ||
      TRX_TYPE_TO_ENTITY[normalizedType] ||
      'CAMPAIGN';

    const subtitleParts = [campaignName, groupName].filter(Boolean);

    const request = _baseRequest({
      amount: value,
      currency,
      entityType,
      entityId: campaignId,
      description: description || subtitleParts.join(' • '),
      referenceNote: tag,
      metadata: {
        trxType: normalizedType || String(campaignType).toUpperCase(),
        groupId,
        campaignId,
        anonymous,
        category,
        categoryLabel,
        title: t('common.contribute'),
        icon: 'heart_fill',
      },
    });

    return _launch(request, {
      title: t('common.contribute'),
      subtitle: subtitleParts.join(' • '),
      icon: 'heart_fill',
      project: campaignName ? { name: campaignName } : null,
      entity: groupName ? { name: groupName } : null,
      setAmount: value,
      tag,
    });
  }

  /** Enroll/fund a save-to-buy campaign. */
  function triggerSaveToBuyContribution({ campaignId, groupId, amount, campaignName = '', groupName = '', tag = '' }) {
    return triggerCampaignContribution({
      campaignId,
      groupId,
      amount,
      campaignType: 'SAVE_TO_BUY',
      campaignName,
      groupName,
      tag,
    });
  }

  // ============================================
  // Ads
  // ============================================

  /**
   * Top up the advertiser billing wallet (ads dashboard balance).
   */
  function triggerAdBudgetTopUp({ advertiserId, amount, currency = 'XOF' }) {
    const value = _requireAmount(amount);
    const request = _baseRequest({
      amount: value,
      currency,
      entityType: 'WALLET_TOPUP',
      entityId: advertiserId || keycloakStore.subject,
      description: t('ads.adBudgetBalance'),
      metadata: {
        walletDomain: 'ADS_BILLING',
        beneficiaryWalletId: advertiserId || keycloakStore.subject,
        icon: 'megaphone_fill',
      },
    });
    return _launch(request, {
      title: t('common.topUp'),
      subtitle: t('ads.avocashAds'),
      icon: 'megaphone_fill',
      setAmount: value,
    });
  }

  /**
   * Fund a specific ad campaign (boost / spend budget).
   */
  function triggerAdCampaignTopUp({ adCampaignId, amount, campaignName = '', currency = 'XOF' }) {
    if (!adCampaignId) throw new Error('adCampaignId is required');
    const value = _requireAmount(amount);
    const request = _baseRequest({
      amount: value,
      currency,
      entityType: 'AD_CAMPAIGN',
      entityId: adCampaignId,
      description: campaignName ? `${t('ads.fundCampaign')} • ${campaignName}` : t('ads.fundCampaign'),
      metadata: { adCampaignId, icon: 'megaphone_fill' },
    });
    return _launch(request, {
      title: t('ads.fundCampaign'),
      subtitle: campaignName,
      icon: 'megaphone_fill',
      project: campaignName ? { name: campaignName } : null,
      setAmount: value,
    });
  }

  // ============================================
  // Marketplace / shops
  // ============================================

  /**
   * Buy-now a single marketplace/shop listing (skips the cart).
   */
  function triggerListingPurchase({ product, variant = null, quantity = 1, shopId, currency }) {
    if (!product?.id) throw new Error('product.id is required');
    const qty = Math.max(1, Number(quantity) || 1);
    const unitPrice = Number(variant?.currentUnitPrice ?? product.currentPrice ?? product.unitPrice);
    const value = _requireAmount(unitPrice * qty);

    const request = _baseRequest({
      amount: value,
      currency: currency || product.currency || 'XOF',
      entityType: 'SHOP_LISTING',
      entityId: product.id,
      description: product.name,
      metadata: {
        listingId: product.id,
        shopId: shopId || product.shopId,
        variantId: variant?.id,
        quantity: qty,
        productName: product.name,
        icon: 'bag_fill',
      },
    });
    return _launch(request, {
      title: t('common.pay'),
      subtitle: product.name,
      icon: 'bag_fill',
      project: { name: product.name },
      setAmount: value,
    });
  }

  /**
   * Pay for a checked-out cart order.
   * The order must already exist server-side (created by the checkout API);
   * pass the returned order object.
   */
  function triggerCartCheckout({ order, cart, amount, currency = 'XOF', deliveryOption = '', deliveryAddress = null, note = '' }) {
    if (!order?.id && !cart?.id) throw new Error('order or cart id is required');
    const value = _requireAmount(
      amount ??
        order?.totalAmount ??
        ((cart?.subtotal || 0) + (cart?.deliveryFee || 0))
    );

    const itemCount = (cart?.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);

    const request = _baseRequest({
      amount: value,
      currency,
      entityType: 'ORDER',
      entityId: order?.id || cart.id,
      description: t('checkout.orderFromItems', { count: itemCount }),
      metadata: {
        orderId: order?.id || undefined,
        cartId: cart?.id,
        deliveryOption,
        deliveryAddress,
        itemCount,
        orderNote: note || undefined,
        icon: 'bag_fill',
      },
    });
    return _launch(request, {
      title: t('common.pay'),
      subtitle: t('checkout.orderTitle', { reference: order?.reference || order?.id || cart?.id }),
      icon: 'bag_fill',
      project: { name: t('shop.myCart') },
      setAmount: value,
    });
  }

  // ============================================
  // Wallets & payouts
  // ============================================

  /**
   * Top up any Avocash wallet (personal standard/business/shared wallet).
   */
  function triggerWalletTopUp({ walletId, walletName = '', amount, currency = 'XOF' }) {
    if (!walletId) throw new Error('walletId is required');
    const value = _requireAmount(amount);
    const request = _baseRequest({
      amount: value,
      currency,
      entityType: 'WALLET_TOPUP',
      entityId: walletId,
      description: `${t('common.topUp')}${walletName ? ` • ${walletName}` : ''}`,
      metadata: { beneficiaryWalletId: walletId, icon: 'bolt_fill' },
    });
    return _launch(request, {
      title: t('common.topUp'),
      subtitle: walletName,
      icon: 'bolt_fill',
      project: walletName ? { name: walletName } : null,
      setAmount: value,
    });
  }

  /**
   * Withdraw / cash out to an external mobile-money number or bank account.
   * Creates a PAY_OUT; defaults to the payout-approval workflow on the
   * backend (requiresApproval=true).
   */
  function triggerPayout({
    walletId,
    amount,
    currency = 'XOF',
    beneficiaryPhoneNumber,
    beneficiaryEmail,
    beneficiaryName,
    bankAccountId,
    description = '',
  }) {
    if (!walletId) throw new Error('walletId is required');
    const value = _requireAmount(amount);
    const request = _baseRequest({
      amount: value,
      currency,
      paymentType: 'PAY_OUT',
      entityType: 'PAYOUT',
      entityId: walletId,
      description: description || t('common.withdraw'),
      metadata: { bankAccountId, icon: 'arrow_up_right_square_fill' },
    });
    request.walletId = walletId; // credited target per PAY_OUT semantics
    request.requiresApproval = true;
    if (beneficiaryPhoneNumber) request.beneficiaryPhoneNumber = String(beneficiaryPhoneNumber).replace(/\D/g, '');
    if (beneficiaryEmail) request.beneficiaryEmail = beneficiaryEmail;
    if (beneficiaryName) request.beneficiaryName = beneficiaryName;

    return _launch(request, {
      title: t('common.withdraw'),
      subtitle: beneficiaryName || beneficiaryPhoneNumber || '',
      icon: 'arrow_up_right_square_fill',
      setAmount: value,
    });
  }

  return {
    triggerCampaignContribution,
    triggerSaveToBuyContribution,
    triggerAdBudgetTopUp,
    triggerAdCampaignTopUp,
    triggerListingPurchase,
    triggerCartCheckout,
    triggerWalletTopUp,
    triggerPayout,
  };
}

export default usePaymentTrigger;
